import * as actions from "./actions";
import * as types from "./types";

import { values } from "lodash";

import { META_OPTION_ACTION } from "consts";

import {
  apiCall,
  fetchImage,
  GET_QUESTIONNAIRES_URL,
  GET_QUESTIONNAIRE_URL,
} from "apis";
import { Navigator } from "router";

import { clearLoadingStatus } from "state/modules/loading";
import { persistState, clearState } from "state/modules/root";

export const setupQuestionnaires = () => async (dispatch, getState) => {
  if (getState().network.isConnected) {
    await dispatch(getQuestionnaires());
  }
};

export const getQuestionnaires = () => async (dispatch, getState) => {
  const { taker, feedback, organization } = getState();
  await dispatch(
    apiCall({
      type: types.GET_QUESTIONNAIRES,
      payload: {
        url: GET_QUESTIONNAIRES_URL(
          organization.id,
          taker.role,
          feedback.group.id,
        ),
        token: taker.token.digest,
        onSuccess: addQuestionnaires,
      },
    }),
  );
};

export const getQuestionnaire = questionnaireId => async (
  dispatch,
  getState,
) => {
  const { organization } = getState();
  await dispatch(
    apiCall({
      type: types.GET_QUESTIONNAIRE,
      payload: {
        url: GET_QUESTIONNAIRE_URL(organization.id, questionnaireId),
        onSuccess: addQuestionnaire,
      },
    }),
  );
};

export const addQuestionnaires = questionnaires => async dispatch => {
  await dispatch(clearState(["questionnaires", "survey"]));
  const promises = [];
  for (const questionnaire of questionnaires) {
    promises.push(dispatch(addQuestionnaire(questionnaire)));
  }
  await Promise.all(promises);
  dispatch(clearLoadingStatus(types.GET_OPTION_IMAGE));
  dispatch(clearLoadingStatus(types.GET_QUESTION_IMAGE));
  dispatch(persistState());
};

export const addQuestionnaire = questionnaire => dispatch => {
  replacePlaceholders(questionnaire);
  denormalizeMetaActionCheck(questionnaire);
  questionnaire.percentage = questionnaire.percentage ?? 10;

  dispatch(actions.addQuestionnaire(questionnaire));
  return Promise.all([
    ...dispatch(cacheQuestionImages(questionnaire)),
    ...dispatch(cacheOptionImages(questionnaire)),
    ...dispatch(cacheThankYouImages(questionnaire)),
    dispatch(cacheSurveyBackground(questionnaire)),
  ]);
};

const cacheQuestionImages = questionnaire => dispatch => {
  const promises = [];
  for (const [
    sIdx,
    section,
  ] of questionnaire.questionnaire_sections.entries()) {
    for (const [qIdx, question] of section.questions.entries()) {
      if (question.images?.length) {
        for (const [iIdx, image] of question.images.entries()) {
          promises.push(
            dispatch(
              fetchImage({
                type: types.GET_QUESTION_IMAGE,
                payload: {
                  url: image.image,
                  onSuccess: img =>
                    dispatch(
                      actions.setQuestionImage(
                        img,
                        questionnaire.id,
                        sIdx,
                        qIdx,
                        iIdx,
                      ),
                    ),
                },
              }),
            ),
          );
        }
      }
    }
  }
  return promises;
};

const cacheOptionImages = questionnaire => dispatch => {
  const promises = [];
  for (const [
    sIdx,
    section,
  ] of questionnaire.questionnaire_sections.entries()) {
    for (const [qIdx, question] of section.questions.entries()) {
      for (const [oIdx, option] of question.options.entries()) {
        if (option.image) {
          promises.push(
            dispatch(
              fetchImage({
                type: types.GET_OPTION_IMAGE,
                payload: {
                  url: option.image,
                  onSuccess: image =>
                    dispatch(
                      actions.setOptionImage(
                        image,
                        questionnaire.id,
                        sIdx,
                        qIdx,
                        oIdx,
                      ),
                    ),
                },
              }),
            ),
          );
        }
      }
    }
  }
  return promises;
};

const cacheThankYouImages = questionnaire => dispatch => {
  const promises = [];
  for (const key of imageKeys) {
    if (questionnaire[key]) {
      promises.push(
        dispatch(
          fetchImage({
            type: types.GET_THANKYOU_IMAGE,
            payload: {
              url: questionnaire[key],
              onSuccess: image =>
                dispatch(
                  actions.setThankyouImage(image, key, questionnaire.id),
                ),
            },
          }),
        ),
      );
    }
  }
  return promises;
};

const denormalizeMetaActionCheck = questionnaire => {
  const MetaActions = values(META_OPTION_ACTION);

  questionnaire.questionnaire_sections.forEach(section =>
    section.questions.forEach(question => {
      question.allow_option_meta_actions = question.options.some(option =>
        MetaActions.includes(option.meta_action),
      );
    }),
  );
};

const imageKeys = [
  "positive_thankyou_image",
  "neutral_thankyou_image",
  "negative_thankyou_image",
  "default_thankyou_image",
];

const cacheSurveyBackground = questionnaire => dispatch => {
  return dispatch(
    fetchImage({
      type: types.GET_SURVEY_BACKGROUND,
      payload: {
        url: questionnaire.background_image,
        onSuccess: image =>
          dispatch(actions.setSurveyBackground(image, questionnaire.id)),
      },
    }),
  );
};

/**
 * At the moment %s is used to refer to name or question selection based on
 * context. Replacing them with seperate placeholders: {name} & {question_id}
 * TODO: Should be handled by backend through migrations
 */
const replacePlaceholders = questionnaire => {
  if (questionnaire.description) {
    const description = replacePlaceholder(questionnaire.description, "{name}");
    questionnaire.description = description;
  }

  questionnaire.questionnaire_sections.forEach(section => {
    if (section.title) {
      section.title = replacePlaceholder(section.title, "{name}");
    }
    section.questions.forEach(question => {
      const placeholder = question.placeholder_from
        ? `{question_${question.placeholder_from}}`
        : "{name}";
      question.text = replacePlaceholder(question.text, placeholder);
      question.text_urdu = replacePlaceholder(question.text_urdu, placeholder);
    });
  });
};

const patterns = [
  { regex: / %s/, prefix: " " },
  { regex: /%s/, prefix: "" },
];
const replacePlaceholder = (text, placeholder) =>
  patterns.reduce((txt, ptrn) => {
    return _replacePlaceholder(txt, ptrn.regex, ptrn.prefix + placeholder);
  }, text);

const _replacePlaceholder = (text, regex, placeholder) => {
  const matches = text?.match(regex);
  if (!matches) {
    return text;
  }
  return matches.reduce(txt => txt.replace(regex, placeholder), text);
};
